import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import { SWUpdatePrompt } from './components/common/SWUpdatePrompt'
import '@appic/ui-components/styles.css'
import '@/styles/select-viewport-scroll.css'
import '@/styles/dialog.css'
import '@/styles/radix-states.css'
import { initUiRuntime } from '@appic/ui-components'
import { installGlobalErrorHandlers } from '@appic/lib/utils/scoutErrorCapture'
import { installConnectivityLogging } from '@appic/lib/utils/connectivityLog'
import { installChunkReloadHandlers } from './lib/chunkReload'
import { scrubBreadcrumb, scrubEvent, scrubTransaction } from './lib/sentryScrub'
import logger from '@/lib/logger'

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  // Performance: sample 20% of transactions in production
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.2 : 1.0,
  // Breadcrumbs: capture fetch, console, clicks for full error context
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.httpClientIntegration(),
  ],
  // Nothing secret leaves through Sentry (lib/sentryScrub): tokens, URL fragments and PII are
  // scrubbed from every breadcrumb, error event and performance event. scrubEvent also keeps the
  // old noise filter: an AbortError (React strict mode cleanup) is not sent.
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubTransaction,
  // Tag all events with app identity
  initialScope: {
    tags: { app: 'clanda-command', frontend: true },
  },
})

// Expose captureException for shared packages (PageErrorBoundary) that can't import @sentry/react
;(window as Window & { __sentryCapture?: typeof Sentry.captureException }).__sentryCapture = Sentry.captureException.bind(Sentry)

// Route uncaught errors + unhandled Promise rejections to scout_activity_log.
// Parallel to Sentry for now; Sentry removal is a follow-up PR (D-23 Supabase SSoT).
installGlobalErrorHandlers()

// Route offline/online transitions to scout_activity_log (went_offline /
// back_online with offline duration). The UI listeners drive the banner; this
// is the observability trace that lets triage correlate a rep dropping offline
// with a burst of failed writes / stalled queue flush.
installConnectivityLogging()

// Recover from stale-chunk dynamic-import failures (PWA deploy-swap → dark
// screen). Wires window 'vite:preloadError' → one-time, loop-guarded reload.
installChunkReloadHandlers()

// Apply UI runtime and global preferences as early as possible (iOS-style accessibility setting).
initUiRuntime()

// Prevent iOS Safari rubber-band/pull-to-refresh from bouncing app surfaces.
if (typeof document !== 'undefined') {
  document.body.classList.add('overscroll-contain-y')
}

// Apply global preferences as early as possible (iOS-style accessibility setting).
// Source of truth: localStorage "layout-preferences" (apps/command/src/hooks/useLayoutPreferences.ts)
try {
  const saved = localStorage.getItem('layout-preferences')
  if (saved) {
    const parsed = JSON.parse(saved) as { appearance?: { density?: string } }

    // Layout density → font scale mapping (Masterslight typography)
    const density = parsed?.appearance?.density
    if (density === 'compact' || density === 'spacious') {
      document.documentElement.setAttribute('data-density', density)
    } else {
      document.documentElement.removeAttribute('data-density')
    }
  }
} catch {
  // ignore
}

// Development-time guard: prevent runtime code from stamping semantic tokens
// like --color-* / --chart-* onto the root element as inline styles.
if (import.meta.env.DEV && typeof document !== 'undefined') {
  const styleAttr = document.documentElement.getAttribute('style')
  const style = styleAttr !== null ? styleAttr : ''
  const semanticVarPattern = /--(?:color|chart|glass|text|lh)-/i
  if (semanticVarPattern.test(style)) {
    logger.error(
      '[semantic-tokens] Detected semantic CSS variables on <html> style attribute. ' +
        'Design tokens must be defined only in @appic/ui-components/styles.css. ' +
        'Offending style:',
      style
    )
    throw new Error('Semantic design tokens must not be set via document.documentElement.style')
  }
}

// Dev-only error boundary to surface render errors (black screen debug)
class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error('RootErrorBoundary', error, info.componentStack)
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    })
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className="w-full max-w-xl rounded-lg border border-danger bg-background shadow-sm p-6"
          >
            <h1 className="text-lg font-semibold text-danger mb-2">
              Anwendungsfehler
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              Ein unerwarteter Fehler ist aufgetreten.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground shadow-sm focus-visible:outline-none"
            >
              Seite neu laden
            </button>
            {import.meta.env.DEV && this.state.error.stack && (
              <details className="mt-4 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">
                  Fehlerdetails (nur Entwicklung)
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all overscroll-contain">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
      <SWUpdatePrompt />
    </RootErrorBoundary>
  </React.StrictMode>,
)
